/** The Construction Kit's tools and the cells each one touches (docs/spec/07 §4). Pure. */

import type { Grid, Point } from './grid';
import { cellAt, inGrid } from './grid';

export type ToolId = 'pencil' | 'line' | 'rect' | 'box' | 'flood' | 'picker';

export interface ToolInfo {
  readonly id: ToolId;
  readonly label: string;
  /** `KeyboardEvent.code` of its shortcut. */
  readonly code: string;
  readonly key: string;
  readonly tip: string;
}

export const TOOLS: readonly ToolInfo[] = [
  { id: 'pencil', label: 'PENCIL', code: 'KeyD', key: 'D', tip: 'PENCIL: PAINT CELL BY CELL (D)' },
  { id: 'line', label: 'LINE', code: 'KeyL', key: 'L', tip: 'LINE: DRAG FROM END TO END (L)' },
  { id: 'rect', label: 'RECT', code: 'KeyR', key: 'R', tip: 'RECTANGLE: DRAG CORNER TO CORNER (R)' },
  { id: 'box', label: 'BLOCK', code: 'KeyB', key: 'B', tip: 'FILLED RECTANGLE: DRAG CORNER TO CORNER (B)' },
  { id: 'flood', label: 'FLOOD', code: 'KeyF', key: 'F', tip: 'FLOOD FILL: REPLACE A CONNECTED AREA (F)' },
  { id: 'picker', label: 'PICKER', code: 'KeyI', key: 'I', tip: 'EYEDROPPER: PICK AN ELEMENT FROM THE CAVE (I)' },
];

export function toolInfo(id: ToolId): ToolInfo {
  return TOOLS.find((t) => t.id === id) ?? (TOOLS[0] as ToolInfo);
}

/** Tools that drag out a shape from an anchor. */
export function isShapeTool(id: ToolId): boolean {
  return id === 'line' || id === 'rect' || id === 'box';
}

/** Every cell on the straight line from a to b (Bresenham), both ends included. */
export function linePoints(a: Point, b: Point): Point[] {
  const out: Point[] = [];
  const dx = Math.abs(b.x - a.x);
  const dy = -Math.abs(b.y - a.y);
  const sx = a.x < b.x ? 1 : -1;
  const sy = a.y < b.y ? 1 : -1;
  let err = dx + dy;
  let { x, y } = a;
  for (;;) {
    out.push({ x, y });
    if (x === b.x && y === b.y) return out;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
}

/** The rectangle with corners a and b: its outline, or every cell in it. */
export function rectPoints(a: Point, b: Point, filled: boolean): Point[] {
  const x0 = Math.min(a.x, b.x);
  const x1 = Math.max(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const y1 = Math.max(a.y, b.y);
  const out: Point[] = [];
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) if (filled || x === x0 || x === x1 || y === y0 || y === y1) out.push({ x, y });
  }
  return out;
}

/** The cells a shape tool covers between anchor and cursor. */
export function shapePoints(tool: ToolId, a: Point, b: Point): Point[] {
  if (tool === 'line') return linePoints(a, b);
  if (tool === 'rect' || tool === 'box') return rectPoints(a, b, tool === 'box');
  return [b];
}

/** The 4-connected area of cells equal to the one at `start`. */
export function floodPoints(g: Grid, start: Point): Point[] {
  const target = cellAt(g, start.x, start.y);
  if (target === undefined) return [];
  const seen = new Uint8Array(g.w * g.h);
  const out: Point[] = [];
  const stack: Point[] = [start];
  seen[start.y * g.w + start.x] = 1;
  while (stack.length > 0) {
    const p = stack.pop() ?? start;
    out.push(p);
    for (const n of [{ x: p.x - 1, y: p.y }, { x: p.x + 1, y: p.y }, { x: p.x, y: p.y - 1 }, { x: p.x, y: p.y + 1 }]) {
      if (!inGrid(g, n.x, n.y) || seen[n.y * g.w + n.x] === 1 || g.cells[n.y * g.w + n.x] !== target) continue;
      seen[n.y * g.w + n.x] = 1;
      stack.push(n);
    }
  }
  return out;
}
